import { FormSelectIdProps, FormSelectInput } from '@nexelem/form-components';
import { useCustomTranslation } from '../../../hooks';
import { useCompetences } from '../../../queryHooks/competenceHooks';
import { CompetenceKind, EvaluationModeType } from '@nexelem/api';
import { z } from 'zod';
import { CUSTOM_ICONS } from '@nexelem/icons';
import { useFormContext } from 'react-hook-form';
import { ProLightFileCertificate } from '@nexelem/icons/ProLightFileCertificate';

type FormCompetenceSelectProps = Omit<FormSelectIdProps, 'label' | 'options' | 'placeholder'> & {
  competenceCategoryId?: number;
};

export const formCompetenceSelectSchema = z.object({
  id: z.number(),
  label: z.string(),
  categoryName: z.string(),
  evaluationMode: z.nativeEnum(EvaluationModeType),
  days: z.number().optional(),
  withRate: z.boolean(),
  hasAuthority: z.boolean(),
  hasFiles: z.boolean(),
});

export type FormCompetenceSelectOption = z.infer<typeof formCompetenceSelectSchema>;

export const FormCompetenceSelect = ({ competenceCategoryId, ...props }: FormCompetenceSelectProps) => {
  const { t, tWrapper } = useCustomTranslation('workers.modals.addCompetence');
  const { data, isLoading } = useCompetences({ competenceCategoryId });
  const startIcon = useIcon(props.name);

  const options = isLoading
    ? []
    : data?.map(
        ({ id, name, categoryName, kind, hasAuthority, hasFiles, evaluationMode }) =>
          ({
            id,
            categoryName,
            hasAuthority,
            hasFiles,
            withRate: kind === CompetenceKind.withRate,
            label: name,
            days: evaluationMode.days,
            evaluationMode: evaluationMode.tpe,
          }) satisfies FormCompetenceSelectOption,
      );

  return (
    <FormSelectInput
      {...props}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      label={tWrapper('competence')}
      loading={isLoading}
      options={options}
      placeholder={t('components.filters.searchOrSelect')}
      StartIcon={startIcon}
      renderOption={(props, option) => (
        <li {...props}>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">{option.label}</div>
            <span className="text-s16 text-text-secondary uppercase">{option.categoryName}</span>
          </div>
        </li>
      )}
    />
  );
};

const useIcon = (name: string) => {
  const competence = useFormContext<Record<string, FormCompetenceSelectOption>>().getValues(name);

  if (competence) {
    return competence.withRate ? CUSTOM_ICONS.ScoreCircleQuarter : ProLightFileCertificate;
  }

  return undefined;
};
